/**
 * API Service Layer for Collections App
 * Handles all API communication with authentication
 */
import { getIdToken, isTokenValid, refreshTokens, redirectToLogin } from './cognitoAuth';

// In development, Vite proxies API requests to avoid CORS
// In production, set VITE_API_URL to the actual API endpoint
const API_BASE_URL = import.meta.env.VITE_API_URL || '';

// Chat uses Lambda Function URL to bypass API Gateway 30s timeout
// Function URLs support up to 15-minute timeouts
const CHAT_URL = import.meta.env.VITE_CHAT_URL || '';

// Spotify account-connect CSRF nonce (COL-330). Shared between Settings.jsx
// (writes it before redirecting to Spotify) and SpotifyCallback.jsx (reads +
// clears it on return) -- a single export so the two can't drift apart.
export const SPOTIFY_OAUTH_STATE_KEY = 'collections_spotify_oauth_state';

// Cache for image URLs to prevent redundant API calls
// Keys are filenames, values are { url, expiresAt }
const imageUrlCache = new Map();
const IMAGE_URL_CACHE_TTL = 55 * 60 * 1000; // 55 minutes (presigned URLs typically expire in 1 hour)
const MAX_CACHE_SIZE = 500; // Prevent unbounded memory growth

// Pending requests for deduplication (prevents duplicate concurrent requests)
const pendingImageRequests = new Map();

// Cleanup stale cache entries to prevent memory leaks
function pruneImageCache() {
  const now = Date.now();
  for (const [key, value] of imageUrlCache) {
    if (value.expiresAt <= now) {
      imageUrlCache.delete(key);
    }
  }
  if (imageUrlCache.size > MAX_CACHE_SIZE) {
    const entries = Array.from(imageUrlCache.entries());
    entries.sort((a, b) => a[1].expiresAt - b[1].expiresAt);
    const toRemove = Math.floor(entries.length / 4);
    for (let i = 0; i < toRemove; i++) {
      imageUrlCache.delete(entries[i][0]);
    }
  }
}

class ApiService {
  constructor() {
    this.baseUrl = API_BASE_URL;
    // Function URL for chat - bypasses API Gateway 30s timeout
    // Falls back to API Gateway if not configured
    this.chatUrl = CHAT_URL;
  }

  getToken() {
    return getIdToken();
  }

  isAuthenticated() {
    return isTokenValid(this.getToken());
  }

  /**
   * Hard auth failure: only reached when refresh itself is impossible or
   * fails, or a freshly-refreshed token is still rejected by the API.
   * Redirects to Cognito hosted UI preserving the current location so the
   * user lands back here after signing in, instead of always on '/'.
   */
  forceLogin() {
    const returnUrl = `${window.location.pathname}${window.location.search}`;
    redirectToLogin(returnUrl);
    throw new Error('Session expired');
  }

  async refreshOrForceLogin() {
    try {
      return await refreshTokens();
    } catch {
      return this.forceLogin();
    }
  }

  /**
   * Attach the bearer token to `fetch(url, options)`. On a 401, refreshes
   * the token (single-flight — concurrent 401s share one refresh) and
   * retries the request exactly once. If refresh fails, or the retried
   * request is still 401'd, redirects to login rather than looping.
   */
  async authorizedFetch(url, options = {}) {
    let token = this.getToken();
    if (!token) {
      throw new Error('Not authenticated');
    }
    if (!isTokenValid(token)) {
      token = await this.refreshOrForceLogin();
    }

    const doFetch = (bearerToken) => fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${bearerToken}`,
        ...options.headers,
      },
    });

    let response = await doFetch(token);

    if (response.status === 401) {
      token = await this.refreshOrForceLogin();
      response = await doFetch(token);
      if (response.status === 401) {
        this.forceLogin();
      }
    }

    return response;
  }

  async request(path, options = {}, { nullOnStatus } = {}) {
    const url = `${this.baseUrl}${path}`;
    const response = await this.authorizedFetch(url, options);

    if (nullOnStatus && response.status === nullOnStatus) return null;

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || error.message || `HTTP ${response.status}`);
    }

    // Validate response is JSON before parsing
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      throw new Error(
        `Expected JSON response but received ${contentType || 'unknown content type'}. ` +
        'This may indicate a configuration issue with the API URL.'
      );
    }

    return response.json();
  }

  /**
   * Like request(), but returns null instead of throwing on 404.
   * Used for endpoints where "not found" is a normal state (e.g., no ranking yet).
   */
  async requestOrNull(path, options = {}) {
    return this.request(path, options, { nullOnStatus: 404 });
  }

  // Categories
  async getCategories() {
    return this.request('/categories');
  }

  // Items
  async getItems({ category, subcategory, limit = 50, offset = 0, includeRawResponse = false } = {}) {
    const params = new URLSearchParams();
    if (category) params.set('category', category);
    if (subcategory) params.set('subcategory', subcategory);
    params.set('limit', limit.toString());
    params.set('offset', offset.toString());
    if (includeRawResponse) params.set('include_raw_response', 'true');
    return this.request(`/items?${params}`);
  }

  async getItem(itemId, { includeRawResponse = false } = {}) {
    const params = new URLSearchParams();
    if (includeRawResponse) params.set('include_raw_response', 'true');
    const queryString = params.toString();
    return this.request(`/items/${itemId}${queryString ? `?${queryString}` : ''}`);
  }

  async createItem(data) {
    return this.request('/items', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteItem(itemId) {
    return this.request(`/items/${itemId}`, { method: 'DELETE' });
  }

  async updateItem(itemId, data) {
    return this.request(`/items/${itemId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async analyzeItem(itemId, options = {}) {
    // Use Function URL if configured to bypass API Gateway 30s timeout
    // Analysis invokes Claude LLM which can take 30-60s
    if (this.chatUrl) {
      const url = `${this.chatUrl}items/${itemId}/analyze`;
      const response = await this.authorizedFetch(url, {
        method: 'POST',
        body: JSON.stringify(options),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.detail || error.message || `HTTP ${response.status}`);
      }

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error(
          `Expected JSON response but received ${contentType || 'unknown content type'}. ` +
          'This may indicate a configuration issue with the Function URL.'
        );
      }

      return response.json();
    }

    // Fallback to API Gateway
    return this.request(`/items/${itemId}/analyze`, {
      method: 'POST',
      body: JSON.stringify(options),
    });
  }

  async getItemAnalyses(itemId, { includeRawResponse = false } = {}) {
    const params = new URLSearchParams();
    if (includeRawResponse) params.set('include_raw_response', 'true');
    const queryString = params.toString();
    return this.request(`/items/${itemId}/analyses${queryString ? `?${queryString}` : ''}`);
  }

  // Rankings
  async getRanking(itemId) {
    return this.requestOrNull(`/items/${itemId}/rankings`);
  }

  async saveRanking(itemId, data) {
    return this.request(`/items/${itemId}/rankings`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteRanking(itemId) {
    return this.request(`/items/${itemId}/rankings`, { method: 'DELETE' });
  }

  async getRankingStats() {
    return this.request('/rankings/stats');
  }

  // Search
  async search(query, options = {}) {
    return this.request('/search', {
      method: 'POST',
      body: JSON.stringify({
        query,
        search_type: options.searchType || 'hybrid',
        top_k: options.topK || 10,
        include_answer: options.includeAnswer ?? true,
        category_filter: options.categoryFilter,
        latest_only: options.latestOnly ?? false,
        include_raw_response: options.includeRawResponse ?? false,
        rerank: options.rerank ?? true,
        scope: options.scope,
      }),
    });
  }

  // Upload
  async getPresignedUploadUrl(filename, contentType = 'image/jpeg', downstreamProcessing = true) {
    return this.request('/uploads/presigned', {
      method: 'POST',
      body: JSON.stringify({
        filename,
        content_type: contentType,
        downstream_processing: downstreamProcessing,
      }),
    });
  }

  async getImageUrl(filename) {
    // Check cache first
    const cached = imageUrlCache.get(filename);
    if (cached && cached.expiresAt > Date.now()) {
      return { url: cached.url };
    }

    // Check if there's already a pending request for this filename (deduplication)
    if (pendingImageRequests.has(filename)) {
      return pendingImageRequests.get(filename);
    }

    // Create the request promise and store it for deduplication
    const requestPromise = (async () => {
      try {
        const result = await this.request(`/images/${filename}`);

        // Prune stale entries before caching
        pruneImageCache();

        // Cache the result
        imageUrlCache.set(filename, {
          url: result.url,
          expiresAt: Date.now() + IMAGE_URL_CACHE_TTL,
        });

        return result;
      } finally {
        // Remove from pending requests when done
        pendingImageRequests.delete(filename);
      }
    })();

    pendingImageRequests.set(filename, requestPromise);
    return requestPromise;
  }

  /**
   * Upload a file and create an item.
   *
   * @param {File} file - The file to upload
   * @param {object} options - Upload options
   * @param {boolean} options.downstreamProcessing - Whether to run analysis/enrichment (default: true)
   * @returns {Promise<{item: object, jobId: string}>} The created item and job ID for tracking
   */
  async uploadFile(file, options = {}) {
    // Get presigned URL
    // API returns: { url, item_id, job_id, key, expires_in }
    const downstreamProcessing = options.downstreamProcessing ?? true;
    const presigned = await this.getPresignedUploadUrl(file.name, file.type, downstreamProcessing);
    const uploadUrl = presigned.url;
    const itemId = presigned.item_id;
    const jobId = presigned.job_id;

    if (!uploadUrl || !itemId) {
      throw new Error('Invalid presigned response: missing url or item_id');
    }

    // Upload directly to S3
    const uploadResponse = await fetch(uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': file.type },
      body: file,
    });

    if (!uploadResponse.ok) {
      throw new Error('Upload failed');
    }

    // Item was already created by the presigned endpoint, fetch it
    const item = await this.getItem(itemId);

    return { item, jobId };
  }

  // Chat
  // itemId (COL-93): pins an item as durable session context. Only needs to
  // be sent on the message that establishes the pin — the server persists
  // it against the session from there, so later messages omit it.
  // progressToken (COL-39): client-minted id for staged status text. The
  // caller polls getChatProgress(progressToken) while this call is still in
  // flight; omit it and this is exact pre-COL-39 behaviour (no progress
  // written server-side, callers see the same bare typing indicator).
  async sendMessage(message, sessionId = null, itemId = null, progressToken = null) {
    const body = { message, session_id: sessionId };
    if (itemId) body.item_id = itemId;
    if (progressToken) body.progress_token = progressToken;

    // Chat requires the Function URL — API Gateway's 29s integration timeout
    // is shorter than chat can take to respond, and the API now rejects
    // POST /chat over API Gateway outright (COL-221). No fallback: a missing
    // VITE_CHAT_URL must fail loudly here, not silently degrade into a 421
    // from the server that looks like an unrelated bug.
    if (!this.chatUrl) {
      throw new Error(
        'Chat is unavailable: VITE_CHAT_URL is not configured. Chat requires ' +
        'the Function URL and no longer falls back to API Gateway (COL-221).'
      );
    }

    // Function URL expects the path to be included
    const url = `${this.chatUrl}chat`;
    const response = await this.authorizedFetch(url, {
      method: 'POST',
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      // Handle both Powertools 'detail' format and standard 'message' format
      throw new Error(error.detail || error.message || `HTTP ${response.status}`);
    }

    // Validate response is JSON before parsing
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      throw new Error(
        `Expected JSON response but received ${contentType || 'unknown content type'}. ` +
        'This may indicate a configuration issue with the Chat Function URL.'
      );
    }

    return response.json();
  }

  async getChatSessions() {
    return this.request('/chat/sessions');
  }

  async deleteChatSession(sessionId) {
    return this.request(`/chat/sessions/${sessionId}`, { method: 'DELETE' });
  }

  async getChatSessionMessages(sessionId) {
    return this.request(`/chat/sessions/${sessionId}/messages`);
  }

  // Staged status polling (COL-39). Always over API Gateway via this.request
  // — never the Function URL (this.chatUrl) — the whole point is a short,
  // separate poll that isn't waiting behind the in-flight /chat POST.
  async getChatProgress(progressToken) {
    return this.request(`/chat/progress/${encodeURIComponent(progressToken)}`);
  }

  async unpinChatItem(sessionId) {
    return this.request(`/chat/sessions/${sessionId}/pin`, { method: 'DELETE' });
  }

  // Annotations
  async getAnnotation(annotationId) {
    return this.request(`/annotations/${annotationId}`);
  }

  async getAnnotationByAnalysis(analysisId) {
    return this.requestOrNull(`/analyses/${analysisId}/annotation`);
  }

  async saveAnnotation(analysisId, data) {
    return this.request(`/analyses/${analysisId}/annotation`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteAnnotation(annotationId) {
    return this.request(`/annotations/${annotationId}`, { method: 'DELETE' });
  }

  async listAnnotations(options = {}) {
    const params = new URLSearchParams();
    if (options.isGolden !== undefined) params.set('is_golden', options.isGolden);
    if (options.limit) params.set('limit', options.limit.toString());
    if (options.offset) params.set('offset', options.offset.toString());
    return this.request(`/annotations?${params}`);
  }

  // Enrichments (Entities)
  async getEnrichments({ status, entityType, entitySubtype, itemId, enrichmentId, cursor, limit = 30 } = {}) {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (entityType) params.set('entity_type', entityType);
    if (entitySubtype) params.set('entity_subtype', entitySubtype);
    if (itemId) params.set('item_id', itemId);
    if (enrichmentId) params.set('enrichment_id', enrichmentId);
    if (cursor) params.set('cursor', cursor);
    params.set('limit', limit.toString());
    return this.request(`/enrichments?${params}`);
  }

  async getEntityTypeCounts() {
    return this.request('/enrichments/counts');
  }

  async getPlaceGeoCounts() {
    return this.request('/enrichments/places/geo-counts');
  }

  // Processing Status
  async getProcessingStatus(jobId) {
    return this.request(`/processing/status?job_id=${jobId}`);
  }

  async getProcessingStatusForItem(itemId) {
    return this.request(`/processing/status?item_id=${itemId}`);
  }

  // Collector (URL-save, COL-137)
  // POST /collect returns either {job_id, platform, status: 'queued'} (202,
  // poll via getCollectStatus) or {status: 'already_collected', item_id,
  // platform, collected_at} (200, if this exact URL was already collected by
  // this user) — callers must handle both.
  async collectUrl(url) {
    return this.request('/collect', {
      method: 'POST',
      body: JSON.stringify({ url }),
    });
  }

  async getCollectStatus(jobId) {
    return this.request(`/collect/${jobId}`);
  }

  // User Collections
  async getMyCollections() {
    return this.request('/collections/mine');
  }

  async createCollection(data) {
    return this.request('/collections', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getCollection(collectionId) {
    return this.request(`/collections/${collectionId}`);
  }

  async updateCollection(collectionId, data) {
    return this.request(`/collections/${collectionId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteCollection(collectionId) {
    return this.request(`/collections/${collectionId}`, {
      method: 'DELETE',
    });
  }

  async addItemsToCollection(collectionId, items) {
    return this.request(`/collections/${collectionId}/items`, {
      method: 'POST',
      body: JSON.stringify({ items }),
    });
  }

  async removeItemsFromCollection(collectionId, items) {
    return this.request(`/collections/${collectionId}/items/remove`, {
      method: 'POST',
      body: JSON.stringify({ items }),
    });
  }

  async reorderCollectionItems(collectionId, items) {
    return this.request(`/collections/${collectionId}/items/reorder`, {
      method: 'PATCH',
      body: JSON.stringify({ items }),
    });
  }

  async getCollectionsForItem(itemId, itemType) {
    const params = new URLSearchParams();
    params.set('item_id', itemId);
    params.set('item_type', itemType);
    return this.request(`/collections/for-item?${params}`);
  }

  // Account (COL-192)
  async signOutAllSessions() {
    return this.request('/account/sign-out-all', { method: 'POST' });
  }

  // Apple Music (COL-246/COL-247/COL-249)
  async getAppleDeveloperToken() {
    return this.request('/music/apple/developer-token');
  }

  async getAppleMusicConnection() {
    return this.request('/music/apple/connection');
  }

  // musicUserToken (MUT) is a live credential -- relayed straight through,
  // never logged, never persisted client-side beyond this call.
  async connectAppleMusic(musicUserToken) {
    return this.request('/music/apple/connect', {
      method: 'POST',
      body: JSON.stringify({ music_user_token: musicUserToken }),
    });
  }

  async disconnectAppleMusic() {
    return this.request('/music/apple/connect', { method: 'DELETE' });
  }

  // Spotify (account connect)
  async getSpotifyAuthorizeUrl(state) {
    const params = new URLSearchParams();
    params.set('state', state);
    return this.request(`/music/spotify/authorize-url?${params}`);
  }

  async getSpotifyConnection() {
    return this.request('/music/spotify/connection');
  }

  async connectSpotify(code) {
    return this.request('/music/spotify/connect', {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
  }

  async disconnectSpotify() {
    return this.request('/music/spotify/connect', { method: 'DELETE' });
  }
}

export const api = new ApiService();
export default api;
